import clsx from 'clsx'
import React, { forwardRef, useId } from 'react'

import { type IconProp } from 'slab/types'

import { CheckboxInput } from './CheckboxInput'

export interface BorderedCheckboxTypes
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'className' | 'size'
  > {
  children?: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  icon?: IconProp;
  label: React.ReactNode;
  size?: 'small' | 'large';
}

export const BorderedCheckbox = forwardRef<
  HTMLInputElement,
  BorderedCheckboxTypes
>(
  (
    { children, description, id, label, size = 'large', icon: Icon, ...props },
    forwardedRef
  ) => {
    const autogeneratedId = useId()
    const inputId = id || autogeneratedId

    return (
      <label
        className={clsx(
          'group/checkbox-container cursor-pointer has-[:disabled]:cursor-default rounded-lg border border-gray-200 has-[:checked]:border-thatch-500 has-[:disabled]:border-gray-200 bg-white hover:bg-gray-50 has-[:focus-visible]:bg-gray-50 has-[:disabled]:bg-gray-100 has-[:checked]:bg-thatch-100 has-[:focus-visible]:has-[:checked]:bg-thatch-100 outline outline-2 outline-offset-2 outline-transparent has-[:focus-visible]:outline-thatch-500 transition shadow hover:shadow-md has-[:disabled]:shadow space-y-4',
          {
            'p-4 h-full block': size === 'large',
            'px-3 h-10 inline-flex items-center': size === 'small',
          }
        )}
      >
        <div className='flex'>
          <div className='h-5 flex items-center'>
            {Icon
              ? (
                <>
                  <input
                    {...props}
                    className='sr-only peer'
                    id={inputId}
                    ref={forwardedRef}
                    type='checkbox'
                  />
                  <span className='text-gray-500 peer-checked:text-thatch-500'>
                    <Icon size={16} />
                  </span>
                </>
                )
              : (
                <CheckboxInput {...props} id={inputId} ref={forwardedRef} />
                )}
          </div>
          <div className='space-y-2 relative'>
            <div className='pl-3 body text-gray-800 group-has-[:disabled]:text-gray-500'>
              {label}
            </div>
            {size === 'large' && !!description && !children && (
              <div className='pl-3 text-gray-500 body-sm leading-normal'>
                {description}
              </div>
            )}
          </div>
        </div>
        {size === 'large' && children && (
          <>
            <div>{children}</div>
            {!!description && (
              <div className='text-gray-500 body-sm leading-normal'>
                {description}
              </div>
            )}
          </>
        )}
      </label>
    )
  }
)
